import React from "react";
import { useLanguage } from "@/App";
import { 
  Search, 
  Megaphone, 
  Palette, 
  Smartphone, 
  GraduationCap, 
  BarChart3,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Target,
  Brush,
  BookOpen
} from "lucide-react";
import { motion } from "framer-motion";

const ExtraServices = () => {
  const { language } = useLanguage();

  const services = [
    {
      icon: Search,
      color: "from-green-500 to-emerald-600",
      accent: "text-green-400",
      bg: "bg-green-500/10",
      title_en: "SEO Optimization",
      title_nl: "SEO Optimalisatie",
      description_en: "Get found on Google. We optimize your website for maximum visibility and organic traffic growth.",
      description_nl: "Word gevonden op Google. Wij optimaliseren je website voor maximale zichtbaarheid en organische groei.",
      price: "€299",
      period_en: "/month",
      period_nl: "/maand",
      features_en: [
        "Keyword research & strategy",
        "On-page SEO optimization",
        "Monthly performance reports",
        "Google Search Console setup",
        "Competitor analysis"
      ],
      features_nl: [
        "Zoekwoorden onderzoek & strategie",
        "On-page SEO optimalisatie",
        "Maandelijkse prestatierapporten",
        "Google Search Console setup",
        "Concurrentie analyse"
      ]
    },
    {
      icon: Megaphone,
      color: "from-pink-500 to-rose-600",
      accent: "text-pink-400",
      bg: "bg-pink-500/10",
      title_en: "Digital Marketing",
      title_nl: "Digitale Marketing",
      description_en: "Reach your ideal customers with targeted campaigns across Google Ads and social media platforms.",
      description_nl: "Bereik je ideale klanten met gerichte campagnes via Google Ads en social media platforms.",
      price: "€499",
      period_en: "/month",
      period_nl: "/maand",
      features_en: [
        "Google Ads management",
        "Social media advertising",
        "Campaign optimization",
        "A/B testing & analytics",
        "Monthly ROI reports"
      ],
      features_nl: [
        "Google Ads beheer",
        "Social media advertenties",
        "Campagne optimalisatie",
        "A/B testing & analytics",
        "Maandelijkse ROI rapporten"
      ]
    },
    {
      icon: Palette,
      color: "from-violet-500 to-purple-600",
      accent: "text-violet-400",
      bg: "bg-violet-500/10",
      title_en: "Logo & Branding",
      title_nl: "Logo & Branding",
      description_en: "Stand out with a unique brand identity. From logo design to complete brand guidelines.",
      description_nl: "Val op met een unieke merkidentiteit. Van logo ontwerp tot complete brand guidelines.",
      price: "€799",
      period_en: "",
      period_nl: "",
      features_en: [
        "Custom logo design (3 concepts)",
        "Color palette & typography",
        "Brand guidelines document",
        "Business card design",
        "Social media templates"
      ],
      features_nl: [
        "Custom logo ontwerp (3 concepten)",
        "Kleurenpalet & typografie",
        "Brand guidelines document",
        "Visitekaartje ontwerp",
        "Social media templates"
      ]
    },
    {
      icon: Smartphone,
      color: "from-blue-500 to-cyan-600",
      accent: "text-blue-400",
      bg: "bg-blue-500/10",
      title_en: "App Development",
      title_nl: "App Ontwikkeling",
      description_en: "Native and cross-platform mobile apps for iOS and Android that engage your users.",
      description_nl: "Native en cross-platform mobiele apps voor iOS en Android die je gebruikers boeien.",
      price: "€4.999",
      period_en: "",
      period_nl: "",
      features_en: [
        "iOS & Android development",
        "Cross-platform (React Native)",
        "UI/UX design included",
        "Push notifications",
        "App Store submission"
      ],
      features_nl: [
        "iOS & Android ontwikkeling",
        "Cross-platform (React Native)",
        "UI/UX design inbegrepen",
        "Push notificaties",
        "App Store publicatie"
      ]
    },
    {
      icon: GraduationCap,
      color: "from-amber-500 to-orange-600",
      accent: "text-amber-400",
      bg: "bg-amber-500/10",
      title_en: "Training & Workshops",
      title_nl: "Training & Workshops",
      description_en: "Empower your team with hands-on AI and web technology training sessions.",
      description_nl: "Versterk je team met hands-on AI en webtechnologie trainingen.",
      price: "€199",
      period_en: "/session",
      period_nl: "/sessie",
      features_en: [
        "AI tools & automation training",
        "Website management basics",
        "CMS training (WordPress/custom)",
        "Analytics & reporting workshops",
        "Custom training programs"
      ],
      features_nl: [
        "AI tools & automatisering training",
        "Website beheer basis",
        "CMS training (WordPress/custom)",
        "Analytics & rapportage workshops",
        "Custom training programma's"
      ]
    },
    {
      icon: BarChart3,
      color: "from-teal-500 to-green-600",
      accent: "text-teal-400",
      bg: "bg-teal-500/10",
      title_en: "Analytics & Reporting",
      title_nl: "Analytics & Rapportage",
      description_en: "Data-driven insights to understand your customers and optimize your digital strategy.",
      description_nl: "Data-gedreven inzichten om je klanten te begrijpen en je digitale strategie te optimaliseren.",
      price: "€199",
      period_en: "/month",
      period_nl: "/maand",
      features_en: [
        "Google Analytics 4 setup",
        "Custom dashboard creation",
        "Conversion tracking",
        "User behavior analysis",
        "Monthly insight reports"
      ],
      features_nl: [
        "Google Analytics 4 setup",
        "Custom dashboard creatie",
        "Conversie tracking",
        "Gebruikersgedrag analyse",
        "Maandelijkse inzichtrapporten"
      ]
    }
  ];

  const getTitle = (s) => language === "nl" ? s.title_nl : s.title_en;
  const getDesc = (s) => language === "nl" ? s.description_nl : s.description_en;
  const getFeatures = (s) => language === "nl" ? s.features_nl : s.features_en;
  const getPeriod = (s) => language === "nl" ? s.period_nl : s.period_en;

  return (
    <section
      id="extra-services"
      data-testid="extra-services-section"
      className="py-32 bg-[#0A0A0A] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,107,0,0.06),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.04),transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-5 py-2 mb-6">
            <Target size={16} className="text-[#FF6B00]" />
            <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-[0.2em]">
              {language === "nl" ? "Groei Je Business" : "Grow Your Business"}
            </span>
          </span>
          <h2
            data-testid="extra-services-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Extra " : "Extra "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-purple-500">
              {language === "nl" ? "Diensten" : "Services"}
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl"
              ? "Vergroot je bereik met onze aanvullende diensten. Van SEO tot branding - alles wat je nodig hebt om te groeien."
              : "Expand your reach with our additional services. From SEO to branding - everything you need to grow."}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              data-testid={`extra-service-card-${index}`}
              className="group relative bg-[#0A0A0A] border border-white/5 p-8 hover:border-[#FF6B00]/30 transition-all"
            >
              {/* Hover glow */}
              <div className={`absolute -inset-px bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-10 blur-xl transition-all duration-500`} />

              <div className="relative z-10">
                {/* Icon */}
                <div className={`w-14 h-14 ${service.bg} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <service.icon size={28} className={service.accent} />
                </div>

                {/* Title & Price */}
                <div className="flex items-start justify-between mb-3">
                  <h3
                    className="text-xl font-bold text-white"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {getTitle(service)}
                  </h3>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <span className={`text-2xl font-black ${service.accent}`}>{service.price}</span>
                  <span className="text-zinc-500 text-sm">{getPeriod(service)}</span>
                </div>

                {/* Description */}
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                  {getDesc(service)}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {getFeatures(service).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle size={14} className={`${service.accent} mt-0.5 flex-shrink-0`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="#contact"
                  className={`inline-flex items-center gap-2 text-sm font-bold ${service.accent} group-hover:underline`}
                >
                  {language === "nl" ? "Meer Info" : "Learn More"}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-zinc-400 mb-6">
            {language === "nl"
              ? "Combineer diensten voor een compleet pakket met korting"
              : "Combine services for a complete package with discount"}
          </p>
          <a
            href="#contact"
            data-testid="extra-services-cta"
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white px-10 py-5 text-sm font-bold tracking-wide hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] transition-all"
          >
            {language === "nl" ? "Vraag Een Offerte Aan" : "Request A Quote"}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ExtraServices;
